/**
 * Compact audio player that renders a waveform instead of a native progress
 * bar. Built on `wavesurfer.js`. Used by the voice detail page (preview
 * variants + saved playback) and — in compact mode — by the voices list rows.
 * Click anywhere on the waveform to seek; the cursor doubles as a progress
 * indicator while playing.
 *
 * The waveform uses the live brand colour (`--portal-primary`) read from
 * computed style at mount, so admin re-branding stays consistent. We resolve
 * to literal hex/rgb values because canvas drawing inside wavesurfer cannot
 * resolve CSS variables.
 */
import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { Pause, Play } from "lucide-react";

/**
 * Module-scoped singleton tracking the most recently started wavesurfer
 * instance. When any `WaveformPlayer` fires "play" we pause whichever
 * player is currently active so only one waveform is ever heard at a time.
 * "pause"/"finish"/unmount clear the slot if they own it.
 */
let activePlayer: WaveSurfer | null = null;
function takeActive(ws: WaveSurfer) {
  if (activePlayer && activePlayer !== ws) {
    try {
      activePlayer.pause();
    } catch {
      /* the previous player may have been destroyed already */
    }
  }
  activePlayer = ws;
}
function releaseActive(ws: WaveSurfer) {
  if (activePlayer === ws) activePlayer = null;
}

type WaveformPlayerProps = {
  src: string;
  /** Waveform pixel height; the surrounding pill is ~24 px taller. */
  height?: number;
  /**
   * Compact preset for tight spaces (e.g. table/list rows): smaller play
   * button, slimmer pill, duration-only time readout, default 22 px waveform.
   */
  compact?: boolean;
  className?: string;
  /** Optional aria-label for the play/pause button. */
  ariaLabel?: string;
  /**
   * When set, only the waveform (not the play button or time readout)
   * becomes an HTML5 drag source; the handler fires on `dragstart`. Used by
   * the voice detail page so the user can drag a waveform onto the
   * transcription tile without accidentally initiating a drag from the
   * play button.
   */
  onWaveformDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
};

function readVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v.length > 0 ? v : fallback;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function WaveformPlayer({
  src,
  height,
  compact = false,
  className,
  ariaLabel,
  onWaveformDragStart,
}: WaveformPlayerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const waveHeight = height ?? (compact ? 22 : 56);
  const playSize = compact ? 26 : 36;
  const iconSize = compact ? 12 : 16;
  const padding = compact ? "3px 8px" : "6px 12px";
  const gap = compact ? 8 : 10;
  const fontSize = compact ? "0.72rem" : "0.78rem";
  const timeMinWidth = compact ? 36 : 78;
  const barWidth = compact ? 1.5 : 2;
  const barGap = compact ? 1.5 : 2;

  useEffect(() => {
    if (!containerRef.current) return;

    const brand = readVar("--portal-primary", "#00A0E3");
    const wave = readVar("--portal-border", "#dee1e2");
    const cursor = readVar("--portal-primary-dark", "#0080b6");

    const ws = WaveSurfer.create({
      container: containerRef.current,
      height: waveHeight,
      waveColor: wave,
      progressColor: brand,
      cursorColor: cursor,
      cursorWidth: compact ? 1 : 2,
      barWidth,
      barGap,
      barRadius: 2,
      normalize: true,
      interact: true,
    });
    wsRef.current = ws;

    setReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);

    const onReady = () => {
      setReady(true);
      setDuration(ws.getDuration());
    };
    const onAudioProcess = () => setCurrentTime(ws.getCurrentTime());
    const onSeeking = () => setCurrentTime(ws.getCurrentTime());
    const onPlay = () => {
      takeActive(ws);
      setIsPlaying(true);
    };
    const onPause = () => {
      releaseActive(ws);
      setIsPlaying(false);
    };
    const onFinish = () => {
      releaseActive(ws);
      setIsPlaying(false);
    };
    const onError = (err: Error | string) => {
      // wavesurfer occasionally raises a transient error after a successful
      // decode (e.g. AbortError when src changes during HMR, or fetch
      // cancellations on a tab switch). Record the message but only flip the
      // player back to "not ready" while we never reached ready in the first
      // place — once we know the duration, keep the play button + time
      // readout live so the user still sees something useful.
      setError(typeof err === "string" ? err : err?.message || "Couldn't load audio");
      setReady((wasReady) => wasReady);
    };

    ws.on("ready", onReady);
    ws.on("audioprocess", onAudioProcess);
    ws.on("seeking", onSeeking);
    ws.on("play", onPlay);
    ws.on("pause", onPause);
    ws.on("finish", onFinish);
    ws.on("error", onError);

    ws.load(src).catch((e: unknown) => {
      onError(e instanceof Error ? e : "Couldn't load audio");
    });

    return () => {
      releaseActive(ws);
      try {
        ws.destroy();
      } catch {
        /* ignore double-destroy on hot-reload */
      }
      wsRef.current = null;
    };
  }, [src, waveHeight, barWidth, barGap, compact]);

  const togglePlay = () => {
    const ws = wsRef.current;
    if (!ws || !ready) return;
    void ws.playPause();
  };

  // Compact rows show a single duration readout (active-during-playback
  // switches to current time) so we don't waste horizontal space on "0:00 /".
  // Only fall back to the "—" placeholder if we never reached `ready` and
  // there's nothing useful to show (true load failure).
  const timeText = ready
    ? compact
      ? formatTime(isPlaying ? currentTime : duration)
      : `${formatTime(currentTime)} / ${formatTime(duration)}`
    : error
      ? "—"
      : compact
        ? "…"
        : "Loading…";

  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        gap,
        background: "#ffffff",
        border: "1px solid var(--portal-border)",
        borderRadius: 999,
        padding,
      }}
    >
      <button
        type="button"
        onClick={togglePlay}
        disabled={!ready}
        aria-label={ariaLabel ?? (isPlaying ? "Pause" : "Play")}
        style={{
          width: playSize,
          height: playSize,
          borderRadius: "50%",
          border: "none",
          background: ready ? "var(--portal-primary)" : "var(--portal-border)",
          color: "#ffffff",
          display: "grid",
          placeItems: "center",
          cursor: ready ? "pointer" : "not-allowed",
          flexShrink: 0,
          padding: 0,
        }}
      >
        {isPlaying ? (
          <Pause size={iconSize} aria-hidden />
        ) : (
          <Play size={iconSize} aria-hidden style={{ marginLeft: 1 }} />
        )}
      </button>

      <div
        ref={containerRef}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          cursor: onWaveformDragStart ? "grab" : undefined,
        }}
        draggable={onWaveformDragStart != null}
        onDragStart={
          onWaveformDragStart
            ? (e) => {
                onWaveformDragStart(e);
                // Build a transparent-background drag image from the live
                // wavesurfer canvases so the user sees only the waveform
                // shape — not the surrounding white pill.
                const target = e.currentTarget;
                const root: ParentNode =
                  (target as HTMLElement).shadowRoot ?? target;
                const canvases = Array.from(
                  root.querySelectorAll("canvas"),
                ) as HTMLCanvasElement[];
                if (canvases.length === 0) return;
                const totalW = canvases.reduce((w, c) => w + c.width, 0);
                const h = Math.max(...canvases.map((c) => c.height));
                if (totalW <= 0 || h <= 0) return;
                const composite = document.createElement("canvas");
                composite.width = totalW;
                composite.height = h;
                const ctx = composite.getContext("2d");
                if (ctx) {
                  let xCursor = 0;
                  for (const c of canvases) {
                    ctx.drawImage(c, xCursor, 0);
                    xCursor += c.width;
                  }
                }
                composite.style.position = "fixed";
                composite.style.top = "-9999px";
                composite.style.left = "-9999px";
                composite.style.pointerEvents = "none";
                document.body.appendChild(composite);
                // Halve the on-screen dimensions so the drag preview tracks
                // close to the cursor at the same display size as the live
                // waveform (devicePixelRatio is folded into canvas.width).
                const dpr = window.devicePixelRatio || 1;
                e.dataTransfer.setDragImage(
                  composite,
                  totalW / (2 * dpr),
                  h / (2 * dpr),
                );
                setTimeout(() => composite.remove(), 0);
              }
            : undefined
        }
        aria-hidden
      />

      <div
        style={{
          fontSize,
          color: "var(--portal-muted)",
          fontVariantNumeric: "tabular-nums",
          minWidth: timeMinWidth,
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {timeText}
      </div>
    </div>
  );
}
